import React, { useEffect, useState } from "react";
import api from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Search, Plus, Pencil, Trash2, Archive, ArchiveRestore, MapPin, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const blank = { name: "", phone: "", email: "", instagram: "", address: "", google_maps_link: "", notes: "" };

export default function Customers() {
  const { isOwner } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null when creating
  const [form, setForm] = useState(blank);

  const [deleteTarget, setDeleteTarget] = useState(null); // customer object
  const [deleteInfo, setDeleteInfo] = useState({ count: 0, loading: false });

  const [selected, setSelected] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState(null); // "archive" | "delete" | null

  const load = () => api.get(`/customers?include_archived=${includeArchived}`).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeArchived]);

  const filtered = rows.filter((c) => {
    if (!q) return true;
    const term = q.toLowerCase();
    return (
      (c.name || "").toLowerCase().includes(term) ||
      (c.phone || "").includes(q) ||
      (c.instagram || "").toLowerCase().includes(term) ||
      (c.email || "").toLowerCase().includes(term)
    );
  });

  const allSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));
  const someSelected = filtered.some((c) => selected.has(c.id));

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map((c) => c.id)));
  };
  const clearSelection = () => setSelected(new Set());

  const openCreate = () => { setEditing(null); setForm(blank); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...blank, ...c }); setDialogOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Name and phone are required"); return; }
    try {
      if (editing) {
        await api.patch(`/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", form);
        toast.success("Customer added");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const askDelete = async (c) => {
    setDeleteTarget(c);
    setDeleteInfo({ count: 0, loading: true });
    try {
      const { data } = await api.get(`/customers/${c.id}/orders-count`);
      setDeleteInfo({ count: data.count, loading: false });
    } catch { setDeleteInfo({ count: 0, loading: false }); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/customers/${deleteTarget.id}`);
      toast.success("Customer deleted");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const confirmArchive = async () => {
    try {
      await api.post(`/customers/${deleteTarget.id}/archive`);
      toast.success("Customer archived. Order history preserved.");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Archive failed");
    }
  };

  const unarchive = async (c) => {
    try { await api.post(`/customers/${c.id}/unarchive`); toast.success("Restored"); load(); }
    catch { toast.error("Failed"); }
  };

  // Bulk operations
  const runBulkArchive = async () => {
    let ok = 0, fail = 0;
    for (const id of selected) {
      try { await api.post(`/customers/${id}/archive`); ok++; } catch { fail++; }
    }
    toast.success(`Archived ${ok}${fail ? ` · ${fail} failed` : ""}`);
    clearSelection(); setBulkAction(null); load();
  };
  const runBulkDelete = async () => {
    let ok = 0, blocked = 0;
    for (const id of selected) {
      try { await api.delete(`/customers/${id}`); ok++; }
      catch { blocked++; }
    }
    toast.success(`Deleted ${ok}${blocked ? ` · ${blocked} skipped (had orders)` : ""}`);
    clearSelection(); setBulkAction(null); load();
  };

  return (
    <div className="space-y-3 animate-fade-in" data-testid="customers-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl">Customers</h1>
          <div className="text-xs text-[#4B5563]">{filtered.length} shown{selected.size > 0 ? ` · ${selected.size} selected` : ""}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-full sm:w-64">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-[#4B5563]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, @handle, email…" className="h-8 pl-8" data-testid="customers-search" />
          </div>
          {isOwner && (
            <>
              <label className="flex items-center gap-1.5 text-xs text-[#4B5563]" data-testid="show-archived-label">
                <Checkbox checked={includeArchived} onCheckedChange={(v) => setIncludeArchived(!!v)} data-testid="show-archived-cb" />
                Show archived
              </label>
              <Button onClick={openCreate} className="bg-[#B86F77] hover:bg-[#A35D65] h-8" data-testid="add-customer-btn">
                <Plus className="h-4 w-4 mr-1" /> Add New Customer
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {isOwner && selected.size > 0 && (
        <div className="bg-[#F3E8E9] border border-[#B86F77]/30 rounded-md p-2 flex items-center justify-between flex-wrap gap-2" data-testid="bulk-actions-bar">
          <div className="text-sm">{selected.size} customer{selected.size !== 1 ? "s" : ""} selected</div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="h-8" onClick={clearSelection} data-testid="bulk-clear-btn">Clear</Button>
            <Button variant="outline" className="h-8 border-[#E8E3DF]" onClick={() => setBulkAction("archive")} data-testid="bulk-archive-btn">
              <Archive className="h-3.5 w-3.5 mr-1" /> Archive
            </Button>
            <Button variant="outline" className="h-8 border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => setBulkAction("delete")} data-testid="bulk-delete-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
            </Button>
          </div>
        </div>
      )}

      <Card className="border-[#E8E3DF] overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="tps-table">
            <TableHeader className="bg-[#FCF9F9]">
              <TableRow>
                {isOwner && (
                  <TableHead className="py-2 w-8">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-cb" aria-label="Select all" />
                  </TableHead>
                )}
                <TableHead className="py-2">Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Instagram</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Maps</TableHead>
                <TableHead>Notes</TableHead>
                {isOwner && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isOwner ? 9 : 7} className="py-8 text-center text-[#4B5563]">
                    No customers {q ? "match your search" : "yet"}.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((c) => (
                <TableRow key={c.id} data-testid={`cust-row-${c.id}`} className={c.archived ? "opacity-60" : ""}>
                  {isOwner && (
                    <TableCell className="py-2">
                      <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)} data-testid={`select-cb-${c.id}`} aria-label={`Select ${c.name}`} />
                    </TableCell>
                  )}
                  <TableCell className="py-2 font-medium">
                    {c.name}
                    {c.archived && <span className="ml-2 text-[10px] uppercase tracking-wider bg-zinc-200 text-zinc-700 px-1.5 py-0.5 rounded">Archived</span>}
                  </TableCell>
                  <TableCell className="py-2 text-sm">{c.phone}</TableCell>
                  <TableCell className="py-2 text-sm">{c.instagram || "—"}</TableCell>
                  <TableCell className="py-2 text-sm">{c.email || "—"}</TableCell>
                  <TableCell className="py-2 text-sm max-w-[16rem] truncate" title={c.address}>{c.address || "—"}</TableCell>
                  <TableCell className="py-2 text-sm">
                    {c.google_maps_link ? (
                      <a href={c.google_maps_link} target="_blank" rel="noreferrer" className="text-[#B86F77] hover:underline inline-flex items-center gap-1" data-testid={`cust-maps-${c.id}`}>
                        <MapPin className="h-3 w-3" /> Open
                      </a>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="py-2 text-sm max-w-[18rem] truncate" title={c.notes}>{c.notes || "—"}</TableCell>
                  {isOwner && (
                    <TableCell className="py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(c)} className="p-1 rounded hover:bg-[#F3E8E9] text-[#B86F77]" data-testid={`cust-edit-${c.id}`} title="Edit">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {c.archived ? (
                          <button onClick={() => unarchive(c)} className="p-1 rounded hover:bg-[#F3E8E9] text-[#4B5563]" data-testid={`cust-unarchive-${c.id}`} title="Restore">
                            <ArchiveRestore className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          <button onClick={() => askDelete(c)} className="p-1 rounded hover:bg-rose-50 text-rose-600" data-testid={`cust-delete-${c.id}`} title="Delete or archive">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Customer" : "Add New Customer"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label className="text-xs">Customer Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-8" data-testid="cust-form-name" /></div>
            <div><Label className="text-xs">Phone Number *</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-8" data-testid="cust-form-phone" inputMode="tel" /></div>
            <div><Label className="text-xs">Instagram Handle</Label><Input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className="h-8" data-testid="cust-form-instagram" placeholder="@handle" /></div>
            <div className="md:col-span-2"><Label className="text-xs">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-8" data-testid="cust-form-email" /></div>
            <div className="md:col-span-2"><Label className="text-xs">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-8" data-testid="cust-form-address" /></div>
            <div className="md:col-span-2"><Label className="text-xs">Google Maps Link</Label><Input type="url" value={form.google_maps_link} onChange={(e) => setForm({ ...form, google_maps_link: e.target.value })} className="h-8" data-testid="cust-form-maps" placeholder="https://maps.google.com/…" /></div>
            <div className="md:col-span-2"><Label className="text-xs">Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cust-form-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="h-8 border-[#E8E3DF]" onClick={() => setDialogOpen(false)} data-testid="cust-form-cancel">Cancel</Button>
            <Button className="h-8 bg-[#B86F77] hover:bg-[#A35D65]" onClick={save} data-testid="cust-form-save">{editing ? "Save Changes" : "Add Customer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete/Archive confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteInfo.loading ? "Checking…" :
                deleteInfo.count > 0 ? "Cannot delete — order history exists"
                                     : "Are you sure you want to delete this customer?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <span className="block mb-2"><strong>{deleteTarget.name}</strong> · {deleteTarget.phone}</span>
              )}
              {deleteInfo.loading && <span>Checking linked orders…</span>}
              {!deleteInfo.loading && deleteInfo.count > 0 && (
                <span>This customer has <strong>{deleteInfo.count}</strong> existing order{deleteInfo.count !== 1 ? "s" : ""}. Delete is not allowed — you can <strong>archive</strong> to hide this customer while preserving their order history.</span>
              )}
              {!deleteInfo.loading && deleteInfo.count === 0 && (
                <span>This customer has no orders. Deletion is permanent.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            {!deleteInfo.loading && deleteInfo.count > 0 && (
              <AlertDialogAction onClick={confirmArchive} className="bg-[#B86F77] hover:bg-[#A35D65]" data-testid="delete-archive-btn">Archive instead</AlertDialogAction>
            )}
            {!deleteInfo.loading && deleteInfo.count === 0 && (
              <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-confirm-btn">Delete</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk archive confirmation */}
      <AlertDialog open={bulkAction === "archive"} onOpenChange={(v) => { if (!v) setBulkAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {selected.size} customer{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>Archived customers are hidden from the default list. Their order history is preserved and they can be restored later.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runBulkArchive} className="bg-[#B86F77] hover:bg-[#A35D65]" data-testid="bulk-archive-confirm">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirmation */}
      <AlertDialog open={bulkAction === "delete"} onOpenChange={(v) => { if (!v) setBulkAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} customer{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>Any customer with existing orders will be <strong>skipped</strong> — only customers with zero orders can be deleted. Orders themselves are never deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runBulkDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="bulk-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
